<template>
  <div class="contenedor">
    <div class="documento" id="documento-pdf">
      <header class="doc-header">
        <img src="@/assets/udi-logo.png" alt="Logo UDI" class="logo" />
        <div class="header-texto">
          <h1 class="nombre-univeridad">
            <span>UNIVERSIDAD</span>
            <span class="line-indent">DE INVESTIGACIÓN</span>
            <span class="line-indent">Y DESARROLLO</span>
          </h1>
        </div>
      </header>

      <section class="seccion-titulo">
        <h1 class="doc-titulo">{{ nombreModelo }}</h1>
      </section>

      <!-- Barra de acciones sobre la tabla -->
      <div class="tabla-toolbar" v-if="componentes.length">
        <button class="btn salir" @click="expandirTodo">Expandir todo</button>
        <button class="btn salir" @click="contraerTodo">Contraer todo</button>
      </div>

      <section class="doc-table" v-if="componentes.length">
        <table>
          <thead>
            <tr>
              <th class="col-nivel">Nivel</th>
              <th class="col-nombre">Componente</th>
              <th class="col-peso">Peso</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="comp in componentes" :key="comp.id_componente">
              <ArbolDocumento
                :nodo="comp"
                :nivel="1"
                :nombres-niveles="nombresNiveles"
                :colapsados="colapsados"
                @toggle="toggleNodo"
              />
            </template>
          </tbody>
        </table>
      </section>

      <section v-else-if="error">
        <p class="doc-error">{{ error }}</p>
      </section>
      <section v-else>
        <p>Cargando componentes...</p>
      </section>

    </div>

    <div class="botones">
      <button class="btn guardar" @click="descargar" :disabled="descargando">
        {{ descargando ? 'Generando...' : 'Descargar' }}
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Presentación documental del modelo, lista para imprimir.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `Documento/:id_modelo/:id_version` (PresentarDocumento) — permiso `modelos.ver`
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, reactive, onMounted, defineComponent, h, PropType, VNode } from 'vue'
import { useRoute } from 'vue-router'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import type Componente from '@/interfaces/Componente'
import type EstructuraModelo from '@/interfaces/EstructuraModelo'
import { useComponentes } from '@/store/GestionComponentes'
import { useModelo } from '@/store/GestionModelo'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import { podarArbolInactivo } from '@/utils/estructuraActiva'

const AZUL = '#0e69a0'
const AMARILLO = '#eea222'  
const route = useRoute()
const idModelo = Number(route.params.id_modelo ?? NaN)
const idVersion = Number(route.params.id_version ?? NaN)
const storeComponentes = useComponentes()
const storeModelo = useModelo()
const storeVersion = useGestionVersiones()
const componentes = ref<Componente[]>([])
const nombreModelo = ref('')
const nombresNiveles = ref<string[]>([])
const descargando = ref(false)
const error = ref<string | null>(null)
const colapsados = reactive<Record<number, boolean>>({})


function colorNivel(nivel: number): string {
  // nivel 1 = azul fuerte; niveles siguientes cada vez más claros
  const tints: Record<number, string> = {
    1: AZUL,
    2: '#dceaf4',
    3: '#eef5fb',
    4: '#f6f9fc'
  }
  return tints[nivel] || '#ffffff'
}
function textoNivel(nivel: number): string {
  return nivel === 1 ? '#ffffff' : (nivel <= 3 ? '#0c447c' : '#185fa5')
}
// Versiones RGB para jsPDF
function rgbNivel(nivel: number): [number, number, number] {
  const map: Record<number, [number, number, number]> = {
    1: [14, 105, 160],
    2: [220, 234, 244],
    3: [238, 245, 251],
    4: [246, 249, 252]
  }
  return map[nivel] || [255, 255, 255]
}
function rgbTextoNivel(nivel: number): [number, number, number] {
  return nivel === 1 ? [255, 255, 255] : (nivel <= 3 ? [12, 68, 124] : [24, 95, 165])
}


/// para expandir
function toggleNodo(id: number) {
  colapsados[id] = !colapsados[id]
}
function expandirTodo() {
  Object.keys(colapsados).forEach(k => { colapsados[Number(k)] = false })
}
function contraerTodo() {
  const marcar = (arr: Componente[]) => {
    arr.forEach(c => {
      if (c.children && c.children.length) {
        if (c.id_componente != null) colapsados[c.id_componente] = true
        marcar(c.children)
      }
    })
  }
  marcar(componentes.value)
}

function ordenarPorOrdinal(lista: Componente[]): Componente[] {
  return [...lista].sort((a, b) => {
    const pa = (a.ordinal ?? '').split('.').map(s => parseInt(s) || 0)
    const pb = (b.ordinal ?? '').split('.').map(s => parseInt(s) || 0)
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const d = (pa[i] ?? 0) - (pb[i] ?? 0)
      if (d !== 0) return d
    }
    return 0
  })
}

function ordenarRecursivo(nodos: Componente[]): Componente[] {
  return ordenarPorOrdinal(nodos).map(n => ({
    ...n,
    children: n.children ? ordenarRecursivo(n.children) : []
  }))
}

/// PDF
async function descargar() {
  if (descargando.value) return
  descargando.value = true

  try {
    const doc = new jsPDF({ unit: 'pt', format: 'letter', orientation: 'portrait' })
    const marginLeft = 60
    const marginTop = 140
    const pageWidth = doc.internal.pageSize.getWidth()

    const img = new Image()
    img.src = '/udi-logo.png'
    await new Promise((resolve, reject) => {
      img.onload = () => resolve(true)
      img.onerror = () => reject(new Error('Error cargando imagen'))
    })

    const head = [['Nivel', 'Componente', 'Peso']]
    const body: { row: (string | number)[], nivel: number }[] = []

    const recorrer = (nodo: Componente, nivel = 1) => {
      const nombreNivel = nombresNiveles.value[nivel - 1] ?? `Nivel ${nivel}`
      const nivelTexto = `${nombreNivel} ${nodo.ordinal ?? ''}`.trim()
      const sangria = '    '.repeat(Math.max(0, nivel - 1))
      body.push({
        row: [
          nivelTexto,
          `${sangria}${nodo.nombre}`,
          nodo.peso != null && !isNaN(Number(nodo.peso)) ? Number(nodo.peso).toFixed(2) + '%' : ''
        ],
        nivel
      })
      if (nodo.children) nodo.children.forEach(hijo => recorrer(hijo, nivel + 1))
    }
    componentes.value.forEach(c => recorrer(c))

    autoTable(doc, {
      head,
      body: body.map(b => b.row),
      startY: marginTop + 20,
      margin: { top: marginTop, bottom: 60, left: marginLeft, right: marginLeft },
      styles: {
        fontSize: 10,
        cellPadding: { left: 10, top: 6, bottom: 6 },
        overflow: 'linebreak',
        lineColor: [255, 255, 255],
        lineWidth: 1
      },
      headStyles: {
        fillColor: [14, 105, 160],
        textColor: [255, 255, 255],
        halign: 'left',
        fontStyle: 'bold',
        cellPadding: { left: 10, top: 7, bottom: 7 }
      },
      columnStyles: {
        0: { cellWidth: 110, halign: 'left' },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 70, halign: 'right', cellPadding: { right: 14, top: 6, bottom: 6, left: 6 } }
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const item = body[data.row.index]
          const nivel = item.nivel
          data.cell.styles.fillColor = rgbNivel(nivel)
          data.cell.styles.textColor = rgbTextoNivel(nivel)
          if (nivel === 1) data.cell.styles.fontStyle = 'bold'
        }
      },
      didDrawCell: (data) => {
        // Barra de acento amarilla a la izquierda de la columna Nivel (col 0)
        if (data.section === 'body' && data.column.index === 0) {
          const nivel = body[data.row.index].nivel
          if (nivel <= 3) {
            const alpha = nivel === 1 ? 1 : 0.45
            const bg = rgbNivel(nivel)
            doc.setFillColor(
              Math.round(238 * alpha + bg[0] * (1 - alpha)),
              Math.round(162 * alpha + bg[1] * (1 - alpha)),
              Math.round(34  * alpha + bg[2] * (1 - alpha))
            )
            doc.rect(data.cell.x, data.cell.y, 3, data.cell.height, 'F')
          }
        }
      },
      didDrawPage: (data) => {
        if (data.pageNumber === 1) {
          doc.addImage(img, 'PNG', marginLeft, 40, 50, 30)
          doc.setFont('helvetica', 'bold')
          doc.setFontSize(13)
          doc.setTextColor(0, 0, 0)
          const textX = marginLeft + 60
          doc.text('UNIVERSIDAD', textX, 50)
          doc.text('DE INVESTIGACIÓN', textX, 65)
          doc.text('Y DESARROLLO', textX, 80)

          doc.setFontSize(14)
          doc.text(nombreModelo.value, pageWidth / 2, 115, { align: 'center' })
        }

        const pageCount = doc.getNumberOfPages()
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(90, 90, 90)
        doc.text(
          `Página ${data.pageNumber} de ${pageCount}`,
          pageWidth - marginLeft,
          doc.internal.pageSize.getHeight() - 20,
          { align: 'right' }
        )
      }
    })

    doc.save('modelo-autoevaluacion.pdf')
  } catch (err) {
    console.error('Error generando PDF:', err)
  } finally {
    descargando.value = false
  }
}

onMounted(async () => {
  window.scrollTo({ top: 0, behavior: 'auto' })

  if (!Number.isFinite(idModelo) || idModelo <= 0) {
    error.value = `Parámetro id_modelo inválido: ${idModelo}`
    return
  }

  // Peticiones independientes en paralelo: datos del modelo y raíz del árbol
  const [modelo, raiz] = await Promise.all([
    storeModelo.getModeloById(idModelo) as Promise<EstructuraModelo | null>,
    storeComponentes.cargarRaizPorModelo(idModelo)
  ])

  if (modelo) {
    nombreModelo.value = modelo.nombre
    if (modelo.niveles) nombresNiveles.value = limpiarNiveles(modelo.niveles)
  }

  if (!raiz?.id_componente) {
    error.value = 'No se encontró la raíz del modelo.'
    return
  }

  // Peticiones independientes en paralelo: árbol completo e IDs activos de versión
  // getIdsComponentesActivos solo se llama cuando hay una versión válida para evitar 404
  const [, activos] = await Promise.all([
    storeComponentes.cargarArbolDesdeRaiz(raiz.id_componente),
    idVersion > 0
      ? storeVersion.getIdsComponentesActivos(idVersion)
      : Promise.resolve(new Set<number>())
  ])

  const raizCompleta = storeComponentes.componentes.find(c => c.id_componente === raiz.id_componente)
  if (!raizCompleta?.children?.length) {
    error.value = 'El modelo no tiene componentes.'
    return
  }

  componentes.value = podarArbolInactivo(ordenarRecursivo(raizCompleta.children), activos)
})

/* Componente recursivo de fila — con caret colapsable, sangría, color y */
const ArbolDocumento = defineComponent({
  name: 'ArbolDocumento',
  props: {
    nodo: { type: Object as PropType<Componente>, required: true },
    nivel: { type: Number, required: true },
    nombresNiveles: { type: Array as PropType<string[]>, required: true },
    colapsados: { type: Object as PropType<Record<number, boolean>>, required: true }
  },
  emits: ['toggle'],
  setup(props, { emit }): () => VNode[] {
    return () => {
      const filas: VNode[] = []
      const nivel = props.nivel
      const bg = colorNivel(nivel)
      const color = textoNivel(nivel)
      const tieneHijos = !!(props.nodo.children && props.nodo.children.length)
      const estaColapsado = props.nodo.id_componente != null ? !!props.colapsados[props.nodo.id_componente] : false
      const padLeft = 16 + (nivel - 1) * 22
      const esFactor = nivel === 1
      const accent =
        esFactor ? `4px solid ${AMARILLO}`
        : (nivel <= 3 ? '4px solid rgba(238,162,34,.45)' : 'none')

      const caret = tieneHijos
        ? h('i', {
            class: 'ti ti-chevron-down',
            style: {
              cursor: 'pointer',
              fontSize: '15px',
              marginRight: '6px',
              display: 'inline-block',
              transition: 'transform .15s',
              transform: estaColapsado ? 'rotate(-90deg)' : 'rotate(0deg)',
              verticalAlign: '-2px'
            },
            onClick: () => emit('toggle', props.nodo.id_componente)
          })
        : h('span', { style: { display: 'inline-block', width: '21px' } })

      const pesoTexto =
        props.nodo.peso != null && !isNaN(Number(props.nodo.peso))
          ? Number(props.nodo.peso).toFixed(2) + '%'
          : ''

      const nombreNivel = props.nombresNiveles[nivel - 1] ?? `Nivel ${nivel}`
      const nivelTexto = `${nombreNivel} ${props.nodo.ordinal ?? ''}`.trim()

      filas.push(
        h('tr', { style: { backgroundColor: bg, borderBottom: esFactor ? '2px solid #fff' : '1px solid #fff' } }, [
          h('td', {
            style: {
              color,
              textAlign: 'left',
              fontVariantNumeric: 'tabular-nums',
              fontWeight: esFactor ? '600' : '400',
              whiteSpace: 'nowrap',
              padding: '10px 16px',
              borderLeft: accent
            }
          }, nivelTexto),
          h('td', {
            style: {
              paddingLeft: `${padLeft}px`,
              paddingTop: '10px',
              paddingBottom: '10px',
              paddingRight: '12px',
              color,
              fontWeight: esFactor ? '600' : '400',
            }
          }, [caret, h('span', props.nodo.nombre)]),
          h('td', {
            style: {
              color,
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              padding: '10px 24px 10px 12px'
            }
          }, pesoTexto)
        ])
      )

      if (tieneHijos && !estaColapsado) {
        const hijosOrdenados = [...props.nodo.children!].sort((a, b) => {
          const pa = (a.ordinal ?? '').split('.').map(s => parseInt(s) || 0)
          const pb = (b.ordinal ?? '').split('.').map(s => parseInt(s) || 0)
          for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
            const d = (pa[i] ?? 0) - (pb[i] ?? 0)
            if (d !== 0) return d
          }
          return 0
        })
        hijosOrdenados.forEach(hijo => {
          filas.push(
            h(ArbolDocumento, {
              nodo: hijo,
              nivel: nivel + 1,
              nombresNiveles: props.nombresNiveles,
              colapsados: props.colapsados,
              onToggle: (id: number) => emit('toggle', id)
            })
          )
        })
      }
      return filas
    }
  }
})
</script>

<style scoped>
.contenedor {
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
}

.documento {
  width: 8.5in;
  min-height: 11in;
  margin: auto;
  padding: 0.7in 1in 1in;
  background: white;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
  font-family: Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.5;
  color: #333;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.doc-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  background: white;
  z-index: 10;
  padding-top: 0.2rem;
  padding-bottom: 0.5rem;
}

.logo { height: 40px; width: auto; flex-shrink: 0; }
.header-texto { display: flex; flex-direction: column; }

.nombre-univeridad {
  margin: 0;
  font-size: 13pt;
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1.1;
}
.nombre-univeridad .line-indent { display: block; }

.doc-titulo {
  margin: 0.25rem 0 0;
  font-size: 12pt;
  text-transform: uppercase;
  color: #333;
  line-height: 1.2;
}

.seccion-titulo {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

/* Toolbar */
.tabla-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.toolbar-label { font-size: 12px; color: #666; }
.toolbar-meta { margin-left: auto; font-size: 11px; color: #999; }
.btn-mini {
  font-size: 12px;
  padding: 4px 12px;
  border: 1px solid #d0d7de;
  background: #fff;
  border-radius: 5px;
  cursor: pointer;
  color: #0c447c;
}
.btn-mini:hover { background: #f2f7fb; border-color: #0e69a0; }

.doc-table {
  overflow-x: auto;
  overflow-y: visible;
  margin-top: 0.25rem;
  border: 0.5px solid #e5e7eb;
  border-radius: 10px;
}

.doc-table table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.doc-table th {
  background: #0e69a0;
  color: #fff;
  text-align: left;
  padding: 12px 16px;
  font-weight: 500;
  font-size: 10.5pt;
}
.doc-table th.col-nivel  { width: 160px; text-align: center; white-space: nowrap; }
.doc-table th.col-peso   { width: 110px; text-align: right; padding-right: 24px; }

.doc-table td {
  padding: 10px 16px;
  word-wrap: break-word;
  font-size: 10.5pt;
}


.doc-error { color: #a32d2d; }

.botones {
  position: sticky;
  top: 12px;
  float: right;
  z-index: 10;
  padding: 0.5rem;
}
.btn {
  padding: 0.9rem 1.7rem;
  font-weight: bold;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}
.btn.guardar {
  width: auto;
  background-color: #eea222;
  color: black;
}
.btn.guardar:hover {
  background-color: #0e69a0;
  color: white;
}
.btn.guardar:disabled { opacity: 0.6; cursor: default; }

.btn.salir {
  padding: 0.35rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  color: #333;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, border-color 0.15s;
}
.btn.salir:hover {
  background-color: #0e69a0;
  border-color: #0e69a0;
  color: white;
}
</style>